'use client'

import { useState } from 'react'
import { Plus, Minus } from 'lucide-react'
import Link from 'next/link'

const FAQS = [
  {
    id: '01',
    q: 'What kind of notifications will I receive?',
    a: `Every notification is a specific, actionable setup — not a vague alert. You get the pair, direction (LONG or SHORT), signal type, entry area, and timeframe. Each one has already passed Algo scoring before it reaches your phone. From settings you can filter by signal type, timeframe, direction, Algo score threshold, and set quiet hours.`,
    topics: ['Push Notifications', 'Breakout', 'CVD Divergence', 'EMA Cross', 'Liquidation', 'Lock Screen'],
  },
  {
    id: '02',
    q: 'What signal types does Algoglide detect?',
    a: `Breakout confirms on key levels, CVD divergence (buy and sell pressure diverging from price action), EMA golden and death crosses, support and resistance bounces, and liquidation cluster approach warnings. Each type has a distinct edge. CVD catches institutional accumulation before price moves. Liquidation signals flag imminent flush or squeeze zones.`,
    topics: ['Breakout', 'CVD Divergence', 'EMA Cross', 'Support Bounce', 'Liquidation Map', 'Algo Filter'],
  },
  {
    id: '03',
    q: 'What are liquidation signals?',
    a: `The liquidation map shows where clusters of leveraged long and short positions sit on the order book. These zones become price magnets — when price approaches a large cluster, it accelerates toward it to flush those positions. Algoglide fires a notification when price is closing in on a cluster dense enough to cause a flush or squeeze. These are among the fastest and most violent moves in the feed.`,
    topics: ['Liquidation Map', 'Open Interest', 'Long Squeeze', 'Short Squeeze', 'Futures', 'Heatmap'],
  },
  {
    id: '04',
    q: 'How many signals will I actually get per day?',
    a: `The raw scanner fires 100+ candidates daily across 400+ pairs. After Algo filters, what reaches your feed is typically 20–40 setups depending on market conditions — less during low-volatility periods. You control the volume: push the score threshold higher and you see fewer, cleaner setups. Lower it for broader coverage. You're never forced to receive everything.`,
    topics: ['Signal Volume', 'Algo Filter', 'Noise Reduction', 'Score Threshold', 'Customisation'],
  },
  {
    id: '05',
    q: 'How fast do signals arrive? Will I miss the move?',
    a: `Signals fire on candle close confirmation — the instant a pattern completes, the notification is sent. On the PWA with push enabled, it hits your lock screen within seconds. For 1h and 4h setups this gives you a genuine first-mover window. Algoglide is built for swing and intraday timeframes where a few seconds don't make or break the trade — not for sub-minute scalping.`,
    topics: ['Latency', 'Candle Close', 'Push Notification', 'Real-time', '1h', '4h'],
  },
  {
    id: '06',
    q: 'Can I control which signals I receive?',
    a: `Yes. From notification settings you filter by signal type (breakout, CVD, EMA, liquidation), timeframe (1h, 4h, 1d), direction (LONG only, SHORT only, or both), and minimum Algo score. You can also set quiet hours by session — so you're not woken up at 3am for a 1h altcoin setup you'd never trade anyway.`,
    topics: ['Notification Filters', 'Timeframe', 'Direction', 'Score Threshold', 'Quiet Hours', 'Customisation'],
  },
  {
    id: '07',
    q: 'Which exchanges are supported?',
    a: `Binance perpetual futures right now. It has the deepest liquidity and the widest altcoin coverage for the type of scanning we do. Bybit and OKX are planned. Spot trading is not currently supported. If a specific exchange is critical for you, let us know — it feeds directly into the roadmap.`,
    topics: ['Binance', 'Perpetual Futures', 'Bybit', 'OKX', 'Spot', 'Roadmap'],
  },
  {
    id: '08',
    q: 'Do I need to connect my exchange to receive signals?',
    a: `No. Signal detection, push notifications, the liquidation map, and all analytics work without connecting an exchange. You only need your Binance API key if you want one-click trade execution directly from the platform. Signals are fully usable as a standalone intelligence layer — your execution stays on your exchange.`,
    topics: ['API Key', 'Optional', 'Signal Feed', 'One-Click Execution', 'Analytics'],
  },
  {
    id: '09',
    q: 'Can I execute trades directly from Algoglide?',
    a: `Yes. With your Binance API key connected, you can open and close positions with a single click from the signal feed or the chart. Entry size, leverage, and TP/SL are configurable per trade. Full automated strategy execution — where Algoglide manages the entire position lifecycle — is on the roadmap.`,
    topics: ['One-Click Execution', 'API Key', 'Leverage', 'TP/SL', 'Strategy', 'Automation'],
  },
  {
    id: '10',
    q: 'Will Algoglide automatically manage my trades or run strategies?',
    a: `Yes — this is core to what Algoglide is building. Phase 1 is Copilot: Algoglide manages your trades with configurable TP, SL, trailing stops, and position scaling once you enter. You stay in control of when to enter, but the position management runs automatically. Phase 2 is Autopilot: fully autonomous trading agents you configure with your own parameters — entry conditions, risk rules, and exit logic — that run 24/7 without you touching anything.`,
    topics: ['Copilot', 'Autopilot', 'Trading Agents', 'TP/SL', 'Automation', 'Strategy'],
  },
  {
    id: '11',
    q: 'What is the Algo score?',
    a: `Algo is our proprietary scoring layer. It evaluates each raw candidate against market structure, volume profile, trend alignment, and pattern quality before it reaches your feed. It's not a single threshold — it's a composite judgment. A setup that looks textbook on the chart can still be rejected if volume or trend context doesn't support it. High Algo score = the setup checks every box.`,
    topics: ['Algo Filter', 'Signal Quality', 'Volume Profile', 'Market Structure', 'Scoring', 'Filter'],
  },
  {
    id: '12',
    q: 'What pairs does Algoglide cover?',
    a: `400+ USDT perpetual futures pairs on Binance, scanned continuously. This includes majors (BTC, ETH, SOL, BNB), mid-caps, and high-volatility alts. The scanner covers everything Binance lists as a perpetual — you're not limited to a curated shortlist. The Algo filter handles the quality control so broad coverage doesn't mean signal noise.`,
    topics: ['400+ Pairs', 'USDT Perp', 'Altcoins', 'Majors', 'Binance Futures', 'Coverage'],
  },
  {
    id: '13',
    q: 'What timeframes are supported?',
    a: `1 hour, 4 hour, and 1 day. These are the timeframes where confirmed breakout and divergence setups have enough follow-through to be actionable before entry is gone. Sub-1h setups close too fast for push notifications to be useful. Daily signals are lower frequency but higher conviction — typically the cleanest setups in the feed.`,
    topics: ['1h', '4h', '1d', 'Timeframe', 'Candle', 'Signal Quality'],
  },
  {
    id: '14',
    q: 'What does the analytics dashboard show?',
    a: `Your full trading performance across all connected positions: realised and unrealised PnL, win rate, Sharpe ratio, max drawdown, equity curve, daily PnL breakdown, average risk-reward, trade history, and position exposure. Everything updates live from your Binance account. No manual imports, no spreadsheets.`,
    topics: ['PnL', 'Win Rate', 'Drawdown', 'Sharpe Ratio', 'Equity Curve', 'Trade History'],
  },
  {
    id: '15',
    q: 'What is the liquidation map exactly?',
    a: `It's a real-time heatmap overlaid on the chart showing where concentrated long and short liquidation levels are positioned based on open interest and estimated entry prices. The darker the cluster, the more positions get liquidated if price reaches that level. Combined with a signal, it tells you not just that a setup exists but whether there's fuel behind it.`,
    topics: ['Liquidation Map', 'Open Interest', 'Heatmap', 'Chart Overlay', 'Futures', 'Binance'],
  },
  {
    id: '16',
    q: 'What is Binance Square signal mining?',
    a: `Algoglide indexes trader profiles and signal posts from Binance Square — finding the accounts with a track record worth following and surfacing their calls into your feed. Instead of paying for 10 different Telegram and Discord groups, you get a curated, filterable stream of the best public signal voices on the platform, with no subscription fees to them.`,
    topics: ['Binance Square', 'Social Signals', 'Curated Feed', 'Community', 'Copy Signals'],
  },
  {
    id: '17',
    q: 'Is there a free plan?',
    a: `No. Algoglide is a subscription product with no free plan or trial. It's $99/month or $999/year (which works out to $83/month). One good signal that you act on covers the monthly cost.`,
    topics: ['Subscription', 'Pricing', 'Monthly', 'Yearly'],
  },
  {
    id: '18',
    q: 'How much does Algoglide cost?',
    a: `$99 per month, or $999 per year — billed annually saves you 16% and works out to $83/month. Both plans include full access to everything: all signal types, push notifications, liquidation map, analytics, Binance Square feed, and one-click execution. There are no feature tiers — you get the whole platform.`,
    topics: ['$99/mo', '$999/yr', 'Subscription', 'Stripe', 'All Features', 'Billing'],
  },
  {
    id: '19',
    q: 'What is included in the subscription?',
    a: `Everything. 400+ pair signal scanning, all signal types, Algo filtering, push notifications to your phone, the liquidation map, full analytics suite (equity curve, drawdown, Sharpe, win rate), Binance Square signal feed, one-click execution, and access to all future features released on your plan tier. No add-ons, no seat limits.`,
    topics: ['All Features', 'Signals', 'Analytics', 'Execution', 'Notifications', 'Liquidation Map'],
  },
  {
    id: '20',
    q: 'Can I cancel anytime?',
    a: `Yes. Cancel from the billing section of your account at any time. You keep access until the end of your paid period — there's no immediate cutoff. Annual subscribers who cancel early don't receive a prorated refund for unused months, but monthly subscribers can cancel at end of billing cycle with no penalty.`,
    topics: ['Cancel', 'Subscription', 'Billing', 'Stripe', 'Access', 'Refund'],
  },
  {
    id: '21',
    q: 'Do you offer refunds?',
    a: `Monthly plans: if you're not satisfied within the first 7 days, contact us and we'll refund the charge. Annual plans: refund within 14 days of initial purchase. After those windows, we don't offer partial refunds — but cancellation stops future charges immediately and you retain access through the period you paid for.`,
    topics: ['Refund Policy', 'Billing', '7 Days', 'Annual', 'Stripe', 'Support'],
  },
  {
    id: '22',
    q: 'Is my Binance API key safe?',
    a: `We request read and trade permissions only — withdrawal permission is never requested and we actively block it in the connection flow. Keys are encrypted at rest. We also recommend creating a restricted API key in Binance that IP-whitelists only our servers — Binance supports this natively and it means your key is useless even if intercepted.`,
    topics: ['Security', 'Encryption', 'API Key', 'IP Whitelist', 'No Withdrawal', 'Binance'],
  },
  {
    id: '23',
    q: 'What data do you store about me?',
    a: `Your account email and authentication info (handled by Stack Auth), your notification preferences, workspace layout, and — if connected — your encrypted API key. We do not store your trade history locally; all position data is fetched live from Binance on request. We do not sell or share any user data.`,
    topics: ['Privacy', 'Data Storage', 'Stack Auth', 'API Key', 'GDPR', 'Security'],
  },
  {
    id: '24',
    q: 'Can I use Algoglide on my phone?',
    a: `Yes — and it's a first-class experience, not an afterthought. Algoglide is a Progressive Web App. Install it from Safari (iOS) or Chrome (Android) in three taps: Share, Add to Home Screen, Allow Notifications. It lives on your home screen, loads instantly, and sends push notifications directly to your lock screen. No App Store, no 200MB download.`,
    topics: ['PWA', 'iOS', 'Android', 'Safari', 'Chrome', 'Push Notifications', 'Home Screen'],
  },
  {
    id: '25',
    q: 'How do I get started?',
    a: `Sign up, verify your email, and you're in. Signals and push notifications work immediately — no exchange connection required. To enable push notifications, install the PWA from your browser and tap Allow when prompted. To use execution, go to Settings and connect your Binance API key. The whole setup from signup to first notification is under two minutes.`,
    topics: ['Onboarding', 'Signup', 'PWA Install', 'API Key', 'Notifications', '2 Minutes'],
  },
  {
    id: '26',
    q: "I'm new to futures trading. Is this suitable for me?",
    a: `Algoglide surfaces setups — it doesn't teach you how to trade. If you're new to futures, the signals are valuable for learning what quality setups look like, but you should understand leverage, liquidation risk, and position sizing before you act on them. The analytics dashboard will show your performance honestly, which is more useful than any course.`,
    topics: ['Futures', 'Education', 'Risk', 'Leverage', 'Liquidation', 'Beginners'],
  },
  {
    id: '27',
    q: 'How is this different from a Telegram or Discord signal group?',
    a: `Signal groups are run by humans who sleep, miss candles, and post screenshots of entries they didn't actually take. By the time a message reaches you, the setup has moved. Algoglide is fully automated — it scans every candle of every pair 24/7, fires the instant a pattern confirms, and delivers it to your lock screen in seconds. You also get context: chart, analytics, liquidation data, and execution in one place instead of a photo in a group of 50,000 strangers.`,
    topics: ['Telegram', 'Discord', 'Automated', 'Manual Signals', 'Speed', 'Quality'],
  },
  {
    id: '28',
    q: 'What is the actual win rate of the signals?',
    a: `We don't publish a fixed win rate and you should be skeptical of anyone who does. Markets are probabilistic — no signal source wins every trade. What Algo does is improve the baseline by removing low-conviction noise before it reaches your feed. Your personal win rate will depend on entry execution, position sizing, and how you manage the trade after entry.`,
    topics: ['Win Rate', 'Accuracy', 'Algo Filter', 'Probabilistic', 'Performance', 'Expectancy'],
  },
  {
    id: '29',
    q: 'Can I use Algoglide alongside my existing setup?',
    a: `Yes. Algoglide works alongside any other tools you use — it doesn't replace your charting software, exchange interface, or risk management workflow. Most users run it as their signal and alert layer, then execute on Binance directly or through Algoglide's one-click interface. There's no conflict with existing automation or bots.`,
    topics: ['Integration', 'TradingView', 'Existing Setup', 'Workflow', 'Compatible', 'Bots'],
  },
  {
    id: '30',
    q: "What's on the roadmap?",
    a: `Copilot is rolling out first — automated trade management (TP, SL, trailing stops, scaling) once you're in a position. After that, Autopilot: custom trading agents you configure with entry conditions, risk parameters, and exit logic that trade completely autonomously. Beyond that: Bybit and OKX support, backtesting against historical signal data, and portfolio-level risk controls. Subscribers get early access as each phase ships.`,
    topics: ['Copilot', 'Autopilot', 'Trading Agents', 'Bybit', 'OKX', 'Backtesting', 'Early Access'],
  },
]

export function FaqSection() {
  const [open, setOpen] = useState<string | null>('04')

  return (
    <section
      className="bg-black py-16 md:py-32"
      style={{ fontFamily: 'var(--font-ppmori, PPMori), sans-serif' }}
    >
      <div className="mx-auto max-w-5xl px-6">

        <div className="mb-12 space-y-2">
          <p className="text-sm uppercase tracking-widest text-white/30">FAQ</p>
          <h2 className="text-4xl font-medium text-white">Questions worth asking.</h2>
        </div>

        <div className="w-full border border-white/10 rounded-md divide-y divide-white/10">
          {FAQS.map((faq) => {
            const isOpen = open === faq.id
            return (
              <div key={faq.id}>
                {/* Trigger */}
                <button
                  onClick={() => setOpen(isOpen ? null : faq.id)}
                  className="flex w-full items-center gap-4 px-4 py-4 text-left hover:bg-white/[0.02] transition-colors"
                >
                  <div className="relative shrink-0 h-6 w-6">
                    <Plus
                      className="absolute inset-0 h-6 w-6 text-white/40 transition-opacity duration-300"
                      style={{ opacity: isOpen ? 0 : 1 }}
                    />
                    <Minus
                      className="absolute inset-0 h-6 w-6 text-white/40 transition-opacity duration-300"
                      style={{ opacity: isOpen ? 1 : 0 }}
                    />
                  </div>
                  <span className="text-base font-medium text-white">{faq.q}</span>
                </button>

                {/* Expanded content */}
                <div
                  className="overflow-hidden transition-all duration-300"
                  style={{ maxHeight: isOpen ? 600 : 0, opacity: isOpen ? 1 : 0 }}
                >
                  <div className="relative grid gap-8 md:grid-cols-2 px-6 pb-10 pt-4 ml-6">
                    {/* Left: answer */}
                    <p className="text-xs leading-relaxed text-white/60">{faq.a}</p>

                    {/* Right: related topics */}
                    <div className="space-y-3 pr-6">
                      <p className="text-[9px] uppercase tracking-widest text-white/30">Related topics</p>
                      <p className="text-[11px] text-white/40 leading-relaxed">
                        {faq.topics.join(', ')}
                      </p>
                    </div>

                    {/* Bottom-right: Get started */}
                    <div className="absolute bottom-0 right-0">
                      <Link
                        href="/auth/signup"
                        className="flex items-center justify-center h-14 px-8 text-xs font-semibold text-black rounded-none transition-opacity hover:opacity-90"
                        style={{ background: '#ffb703' }}
                      >
                        Get started
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

      </div>
    </section>
  )
}
